// import { observable, action, makeObservable } from 'mobx'

import SlidePlayer from "./SlidePlayer";
import type { LayerSerialized, PresentationSerialized, SlideSerialized } from "@scrolly/post-types";
import { LayerType } from "@scrolly/post-types";
import type { PresentationRender } from "./PresentationRender";
import RendererStore from "./RendererStore";
import type { LayerRender } from "./LayerRender";
import { layer3D } from "./Layer3D";
import isEqual from "lodash/isEqual";

/* eslint-disable @typescript-eslint/no-explicit-any */
const buildTreeSimple = (layer: LayerRender, layersAndGroups: Array<LayerRender>): LayerRender => {
    if (!(layer.type === LayerType.group || layer.type === LayerType.object3d)) {
        return layer;
    } else {
        // TODO: remove me in the future
        let layerIds: Array<string> = layer.content as any;
        if ((layer.content as any).layerIds) {
            const { layerIds: layerIdsNew } = layer.content as any;
            layerIds = layerIdsNew;
        }
        // TODO remove;
        if (!Array.isArray(layerIds)) {
            return layer;
        }
        const _layers = layerIds.map((id) => layersAndGroups.find((g) => g.id === id)).filter(Boolean);
        (layer as any).layers = _layers;
        (layer as any).layers.forEach((l: TODOAny) => buildTreeSimple(l, layersAndGroups));
    }
    return layer;
};

function presentationRenderFromJson(presentationSerialized: PresentationSerialized): PresentationRender {
    const convert = (layer: LayerSerialized): LayerRender => {
        const layer3DWithTime = (t: TODOAny) => layer3D(layer.animationsGroups, t);
        return {
            pivotPoint: [0, 0, 0],
            ...layer,
            setObject: () => null,
            visible: layer.visible !== false,
            layer3D: layer3DWithTime,
            // layer3D,
            showPivotPoint: false,
        };
    };
    const { groups } = presentationSerialized;
    const { layers } = presentationSerialized;
    const groupsAndLayers = [...groups, ...layers].map(convert);
    const tree = buildTreeSimple(groupsAndLayers[0], groupsAndLayers);
    return {
        groupTree: tree,
        presentationOptions: presentationSerialized.presentationOptions,
        slides: presentationSerialized.slides,
    };
}

export default class PlayablePresentation {
    isRunning = false;

    presentationSerialized: PresentationSerialized;
    presentationRender: PresentationRender;
    slidePlayer: SlidePlayer;
    renderer: RendererStore;
    slideTimingsSubscriber: (() => void)[] = [];

    cachedChaptersAndSlidesAsNestedTimepoints: Array<Array<number>> = [];

    constructor(presentation: PresentationSerialized, renderer: RendererStore) {
        this.presentationSerialized = presentation;
        // this should be made asynchronous, but too complicated for now
        this.presentationRender = presentationRenderFromJson(this.presentationSerialized);
        this.slidePlayer = new SlidePlayer(this.presentationRender.slides.map((s) => s.from));
        this.emitSlideTimingsChanges();
        this.renderer = renderer;
        this.start = this.start.bind(this);
        this.stop = this.stop.bind(this);
    }

    setPresentation = (presentation: PresentationSerialized) => {
        this.presentationSerialized = presentation;
        this.presentationRender = presentationRenderFromJson(this.presentationSerialized);
        this.emitSlideTimingsChanges();
        this.updateSlideTimings(this.presentationRender.slides);
    };

    updateSlideTimings = (slides: Array<SlideSerialized>) => {
        this.slidePlayer.setSlideTimings(slides.map((s) => s.from));
    };

    start = (animate: (timer: number) => any) => {
        this.isRunning = true;
        this.slidePlayer.startLoop(animate);
    };

    stop = () => {
        this.isRunning = false;
        this.slidePlayer.stopLoop();
        this.slidePlayer.setTimer(0);
    };

    subscribeSlideTimings = (listener: () => void) => {
        this.slideTimingsSubscriber = [...this.slideTimingsSubscriber, listener];
        return () => {
            this.slideTimingsSubscriber = this.slideTimingsSubscriber.filter((l) => l !== listener);
        };
    };

    emitSlideTimingsChanges = () => {
        this.slideTimingsSubscriber.forEach((l) => l());
    };

    getChaptersAndSlidesAsNestedTimepointsSnapshot = () => {
        const newSlideTimings = this.chaptersAndSlidesAsNestedTimepoints();
        // Memoize the snapshot as recommended by react for the use with useSyncExternalStore
        if (!isEqual(this.cachedChaptersAndSlidesAsNestedTimepoints, newSlideTimings)) {
            this.cachedChaptersAndSlidesAsNestedTimepoints = newSlideTimings;
        }
        return this.cachedChaptersAndSlidesAsNestedTimepoints;
    };

    chaptersAndSlidesAsNestedTimepoints = () => {
        const { slides } = this.presentationRender;
        const chapterIndices = slides.map((slide, i) => (slide.isChapter ? i : -1)).filter((i) => i >= 0);

        // Some AI generated templates might be missing the initial chapter. In that case we add it here.
        if (chapterIndices.length === 0) {
            chapterIndices.push(0);
        }
        const slideTimes = slides.map((slide) => slide.from);

        return chapterIndices.map((chapterStartIndex, i, allIndices) => {
            if (i < allIndices.length - 1) {
                const nextChapterStartIndex = allIndices[i + 1];
                return slideTimes.slice(chapterStartIndex, nextChapterStartIndex);
            }
            return slideTimes.slice(chapterStartIndex);
        });
    };
}
